import type {
  BooleanFeatureFlagName,
  TenantFeatureFlags,
} from '@repo/api/common/feature-flags';
import { useTenantMe } from '@/api/tenants/use-tenant-me';

/**
 * Un booleano `isX` per ogni feature flag on/off. Il tipo mappato rende
 * obbligatorio aggiungere il campo quando nasce un flag nuovo: dichiararlo
 * nello schema condiviso e non qui non compila.
 */
export type TenantFeatureFlagStates = {
  [K in BooleanFeatureFlagName as `is${Capitalize<K>}`]: boolean;
};

/**
 * Tutti i flag spenti: è il valore finché il tenant carica. Vive come
 * costante di modulo, non come letterale ricreato a ogni render. Il tipo
 * `TenantFeatureFlags` lo rende esaustivo a compile time.
 */
const NO_FEATURE_FLAGS: TenantFeatureFlags = {
  onboardingAiOpsPanel: false,
  warehouseEnabled: false,
  f24PaymentsEnabled: false,
  forecastHistoryEnabled: false,
  serviceContractsEnabled: false,
  commesseEnabled: false,
  workReportsEnabled: false,
  hotelEnabled: false,
  machinesEnabled: false,
  computerGrossEnabled: false,
  devTechnicianSeatsEnabled: false,
};

/**
 * Flag del tenant → booleani `isX`. I rapportini registrano licenze e
 * addebiti sui contratti di servizio: senza contratti restano spenti anche
 * se il loro flag è acceso (dipendenza D7, applicata anche dal backend); il
 * flag di sviluppo dei posti tecnico vale solo con i rapportini attivi.
 */
export function resolveTenantFeatureFlagStates(
  flags: TenantFeatureFlags
): TenantFeatureFlagStates {
  return {
    isOnboardingAiOpsPanel: flags.onboardingAiOpsPanel,
    isWarehouseEnabled: flags.warehouseEnabled,
    isF24PaymentsEnabled: flags.f24PaymentsEnabled,
    isForecastHistoryEnabled: flags.forecastHistoryEnabled,
    isServiceContractsEnabled: flags.serviceContractsEnabled,
    isCommesseEnabled: flags.commesseEnabled,
    isWorkReportsEnabled:
      flags.workReportsEnabled && flags.serviceContractsEnabled,
    isHotelEnabled: flags.hotelEnabled === true,
    isMachinesEnabled: flags.machinesEnabled === true,
    isComputerGrossEnabled: flags.computerGrossEnabled === true,
    isDevTechnicianSeatsEnabled:
      flags.devTechnicianSeatsEnabled === true &&
      flags.workReportsEnabled &&
      flags.serviceContractsEnabled,
  };
}

/**
 * Legge i feature flag del tenant selezionato (default: tutti OFF).
 *
 * La fonte è `GET /tenants/:tenantId/me` — il tenant visto dal membro, già
 * caricato dal layout e letto dagli hook gemelli `useInvoicingMode` /
 * `useSubscriptionStatus`. NON i tenant settings: quelli stanno dietro
 * `canAccessSettings`, spento di default per USER e GUEST, e chi non l'aveva
 * vedeva ogni feature disattivata pur avendo i permessi per usarla. E nemmeno
 * il profilo utente: un feature flag è un fatto del tenant, non della persona.
 *
 * `isLoading` è quello della query: finché è `true` i flag sono tutti
 * `false` — chi mostra affordance costose (bottoni di pagamento, voci di menu)
 * deve aspettare, non dedurre che la feature sia spenta.
 *
 * VINCOLO: da `@repo/api/common/feature-flags` si importa SOLO il tipo, mai un
 * valore (nemmeno `FEATURE_FLAGS`). `@repo/api` compila in CommonJS e il
 * percorso profondo `common/feature-flags` non è nella sua mappa `exports`,
 * quindi a runtime Vite non riesce a importarne i valori; i tipi invece
 * vengono cancellati in compilazione. I nomi dei flag restano letterali,
 * controllati dai tipi.
 */
export function useTenantFeatureFlags(): TenantFeatureFlagStates & {
  isLoading: boolean;
} {
  const { data: tenant, isLoading } = useTenantMe();
  const flags = tenant?.featureFlags ?? NO_FEATURE_FLAGS;

  return { ...resolveTenantFeatureFlagStates(flags), isLoading };
}
